import React from 'react'
import { Router, Switch, Route, Redirect } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { history } from './history'
import FullpageLayout from './layouts/FullpageLayout'
import { ContextLayout } from './utility/context/Layout'
import {
  QueryParamProvider,
  transformSearchStringJsonSafe,
} from 'use-query-params'

import {
  Login,
  ForgotPassword,
  ResetPassword,
  InviteAccept,
} from './pages/authentication'
import { Error404 } from './pages/miscellaneous'
import {
  BulkEditGeneralInfo,
  BulkEditImport,
  BulkEditImages,
  BulkEditSocialMedia,
} from './pages/app/locations'
import {
  Event as NewEventPost,
  Alert as NewAlertPost,
  CTA as NewCTAPost,
  Offer as NewOfferPost,
} from './pages/app/Posts'
import Reviews from './pages/app/Reviews'
import Questions from './pages/app/Questions'
import CreateAccount from './pages/app/Accounts/create'
import Accounts from './pages/app/Accounts/index'
import Projects from './pages/app/Projects/index'
import CreateProject from './pages/app/Projects/create'
import CreateUsers from './pages/app/Users/create'
import UserProfile from './pages/app/Users/profile'
import ListUsers from './pages/app/Users/index'
import _ from 'lodash'
import Insights from './pages/app/Insights'
import InsightsKeywords from './pages/app/Insights/keywords'
import InsightsLocations from './pages/app/Insights/locations'
import Notifications from './pages/app/Notifications'
import Schedules from './pages/app/Schedules'
import Publications from './pages/app/Publications'
import AnsweredQuestions from './pages/app/AnsweredQuestions'
import SingleQuestion from './pages/app/Questions/SingleQuestion'
import SingleReview from './pages/app/Reviews/SingleReview'
import LocationSingleEdit from './pages/app/locations/SingleEdit'
import LocationsOverview from './pages/app/locations/overview'
import HomePage from './pages/app/Home'
import PendingEdits from './pages/app/locations/pendingEdits'
import LocationsScore from './pages/app/locations/score'
import AccountSettings from './pages/app/Accounts/settings'
import { ADMIN, CLIENT_ADMIN, SUPER_ADMIN } from './constants/roles'
import EditScheduledPost from './pages/app/Posts/Edit'
import NPS from './pages/app/NPS'
import TutorialHub from './pages/app/Tutorials/hub'
import TutorialModule from './pages/app/Tutorials/module'
import TutorialPage from './pages/app/Tutorials/tutorial'
import TutorialCreate from './pages/app/Tutorials/create'
import ListTutorials from './pages/app/Tutorials/tutorials'

const AppRoute = ({
  component: Component,
  fullLayout,
  isAppAdmin: appAdminRoute,
  isAdmin: isAdminRoute,
  requiredPermission,
  isPublic,
  ...rest
}) => {
  const user = useSelector(state => state.user?.profile)
  const isAuth = useSelector(state => state.auth.signed)
  const userPermissions = useSelector(
    state => state.user?.profile?.userPermission
  )

  const userProfile = useSelector(state => state.user?.profile)
  const userIsAppAdmin = userProfile?.role === SUPER_ADMIN
  const userIsClientAdmin = userProfile?.role === CLIENT_ADMIN
  const userIsAdmin = userProfile?.role === ADMIN

  if (isAdminRoute && !userIsClientAdmin && !userIsAppAdmin && !userIsAdmin) {
    return <Redirect to="/" />
  }

  if (appAdminRoute && !userIsAppAdmin) {
    return <Redirect to="/" />
  }

  if (requiredPermission) {
    const permissions = userPermissions
    const havePermission = _.isArray(requiredPermission)
      ? requiredPermission
          .map(permission => _.get(permissions, permission, 'not-permited'))
          .every(value => !!value && value !== 'not-permited')
      : _.get(permissions, requiredPermission, false)
    if (!havePermission) return <Redirect to="/" />
  }

  if (!isPublic && !isAuth) return <Redirect to="/login" />

  if (isPublic && isAuth) return <Redirect to="/" />

  return (
    <Route
      {...rest}
      render={props => {
        return (
          <ContextLayout.Consumer>
            {context => {
              const LayoutTag =
                fullLayout === true
                  ? context.fullLayout
                  : context.state.activeLayout === 'horizontal'
                  ? context.horizontalLayout
                  : context.VerticalLayout

              return isPublic ? (
                <FullpageLayout>
                  <Component {...props} />
                </FullpageLayout>
              ) : (
                <LayoutTag {...props} permission={user}>
                  <Component {...props} />
                </LayoutTag>
              )
            }}
          </ContextLayout.Consumer>
        )
      }}
    />
  )
}

const queryStringifyOptions = {
  transformSearchString: transformSearchStringJsonSafe,
  skipNull: true,
  skipEmptyString: true,
  arrayFormat: 'comma',
}

function AppRouter() {
  return (
    <Router history={history}>
      <QueryParamProvider
        ReactRouterRoute={Route}
        stringifyOptions={queryStringifyOptions}
      >
        <Switch>
          <AppRoute exact path="/" component={HomePage} />

          <AppRoute
            isPublic
            path="/forgot-password"
            component={ForgotPassword}
          />
          <AppRoute isPublic path="/reset-password" component={ResetPassword} />
          <AppRoute isPublic path="/login" component={Login} fullLayout />
          <AppRoute
            isPublic
            path="/invite/accept"
            component={InviteAccept}
            fullLayout
          />

          <AppRoute path="/locations/list" component={LocationsOverview} />
          <AppRoute path="/locations/overview" component={LocationsOverview} />
          <AppRoute
            path="/locations/pending"
            component={PendingEdits}
            requiredPermission={['edits']}
          />
          <AppRoute path="/locations/score" component={LocationsScore} />

          <AppRoute
            path="/insights/keywords"
            component={InsightsKeywords}
            requiredPermission={['keywords']}
          />
          <AppRoute
            path="/insights/locations"
            component={InsightsLocations}
            requiredPermission={['insights']}
          />
          <AppRoute
            path="/insights"
            component={Insights}
            requiredPermission={['insights']}
          />

          <Redirect from="/newinsights" to="insights" />

          <AppRoute
            exact
            path="/posts/event"
            component={NewEventPost}
            requiredPermission={['posts']}
          />
          <AppRoute
            exact
            path="/posts/cta"
            component={NewCTAPost}
            requiredPermission={['posts']}
          />
          <AppRoute
            exact
            path="/posts/alert"
            component={NewAlertPost}
            requiredPermission={['posts']}
          />
          <AppRoute
            exact
            path="/posts/offer"
            component={NewOfferPost}
            requiredPermission={['posts']}
          />
          <AppRoute
            exact
            path="/schedule/:scheduledPostId"
            component={EditScheduledPost}
            requiredPermission={['posts']}
          />
          <AppRoute
            exact
            path="/locations/:locationId/edit"
            requiredPermission={['infos']}
            component={LocationSingleEdit}
          />

          <AppRoute
            exact
            path="/locations/edit/general"
            requiredPermission={['infos']}
            component={BulkEditGeneralInfo}
          />
          <AppRoute
            exact
            path="/locations/edit/social-media"
            requiredPermission={['infos']}
            component={BulkEditSocialMedia}
          />
          <AppRoute
            exact
            path="/locations/edit/images"
            requiredPermission={['infos']}
            component={BulkEditImages}
          />
          <AppRoute
            exact
            path="/locations/edit/import"
            requiredPermission={['infos']}
            component={BulkEditImport}
          />

          <AppRoute
            exact
            path="/reviews"
            component={Reviews}
            requiredPermission={['reviews']}
          />

          <AppRoute
            exact
            path="/reviews/:reviewId"
            component={SingleReview}
            requiredPermission={['reviews']}
          />

          <AppRoute
            exact
            path="/questions"
            component={Questions}
            requiredPermission={['questions']}
          />

          <AppRoute
            exact
            path="/questions/:questionId"
            component={SingleQuestion}
            requiredPermission={['questions']}
          />

          <AppRoute
            exact
            path="/locations/questions"
            component={Questions}
            requiredPermission={['questions']}
          />

          <AppRoute
            exact
            path="/locations/question/:questionId"
            component={SingleQuestion}
            requiredPermission={['questions']}
          />

          <AppRoute
            exact
            isAdmin
            path="/account/settings"
            component={AccountSettings}
          />

          <AppRoute
            exact
            isAdmin
            path="/templates/reviews"
            component={() => <Redirect to="/account/settings?tab=reviews" />}
          />

          <AppRoute
            exact
            isAdmin
            path="/templates/questions"
            component={() => <Redirect to="/account/settings?tab=questions" />}
          />

          <AppRoute
            exact
            isAppAdmin
            path="/accounts/new"
            component={CreateAccount}
          />
          <AppRoute
            exact
            isAppAdmin
            path="/accounts/:accountId"
            component={CreateAccount}
          />
          <AppRoute exact isAppAdmin path="/accounts" component={Accounts} />

          <AppRoute exact path="/projects" component={Projects} />
          <AppRoute
            exact
            isAppAdmin
            path="/projects/new"
            component={CreateProject}
          />
          <AppRoute
            isAppAdmin
            path="/projects/:projectId"
            component={CreateProject}
          />

          <AppRoute exact path="/profile" component={UserProfile} />
          <AppRoute exact isAdmin path="/users/new" component={CreateUsers} />
          <AppRoute
            exact
            isAdmin
            path="/users/:userId"
            component={CreateUsers}
          />
          <AppRoute exact isAdmin path="/users" component={ListUsers} />

          <AppRoute
            exact
            path="/notifications"
            component={Notifications}
            requiredPermission={['notifications']}
          />

          <AppRoute
            exact
            path="/publications"
            component={Publications}
            requiredPermission={['schedules']}
          />
          <AppRoute
            exact
            path="/schedules"
            component={Schedules}
            requiredPermission={['schedules']}
          />
          <AppRoute exact path="/nps" component={NPS} />

          <AppRoute exact path="/help-center" component={TutorialHub} />
          <AppRoute
            exact
            isAppAdmin
            path="/tutorial/create"
            component={TutorialCreate}
          />
          <AppRoute
            exact
            isAppAdmin
            path="/tutorial/:tutorialId/edit"
            component={TutorialCreate}
          />
          <AppRoute
            exact
            path="/tutorial/:tutorialId"
            component={TutorialPage}
          />
          <AppRoute
            exact
            path="/tutorial/module/:moduleName"
            component={TutorialModule}
          />
          <AppRoute
            exact
            isAppAdmin
            path="/tutorials"
            component={ListTutorials}
          />

          <AppRoute
            path="/answered-questions"
            component={AnsweredQuestions}
            requiredPermission={['infos']}
          />

          <AppRoute component={Error404} fullLayout />
        </Switch>
      </QueryParamProvider>
    </Router>
  )
}

export default AppRouter
